import React from 'react';

export type ReviewStatusTab =
  | 'all'
  | 'needs_review'
  | 'in_review'
  | 'held_retry'
  | 'approved_internal'
  | 'delivered'
  | 'feedback';

export interface AdminReviewFilterState {
  tab: ReviewStatusTab;
  workflowStatus: string;
  deliveryStatus: string;
  autoGateStatus: string;
  searchQuery: string;
  limit: number;
}

interface AdminReviewFiltersProps {
  filters: AdminReviewFilterState;
  onChange: (next: AdminReviewFilterState) => void;
  onRefresh: () => void;
  loading?: boolean;
}

const TABS: Array<{ id: ReviewStatusTab; label: string }> = [
  { id: 'needs_review', label: 'Needs Review' },
  { id: 'in_review', label: 'In Review' },
  { id: 'held_retry', label: 'Held / Retry' },
  { id: 'approved_internal', label: 'Approved Internal' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'all', label: 'All' },
];

const AdminReviewFilters: React.FC<AdminReviewFiltersProps> = ({
  filters,
  onChange,
  onRefresh,
  loading,
}) => {
  const setField = <K extends keyof AdminReviewFilterState>(
    key: K,
    value: AdminReviewFilterState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const active = filters.tab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setField('tab', tab.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                active
                  ? 'bg-primary-600 text-white'
                  : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200 dark:bg-secondary-800 dark:text-secondary-200 dark:hover:bg-secondary-700'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <input
          type="search"
          placeholder="Search upload ID, team, gamertag…"
          value={filters.searchQuery}
          onChange={(e) => setField('searchQuery', e.target.value)}
          className="input-field xl:col-span-2"
        />
        <select
          value={filters.workflowStatus}
          onChange={(e) => setField('workflowStatus', e.target.value)}
          className="input-field"
        >
          <option value="">Any workflow status</option>
          <option value="needs_review">needs_review</option>
          <option value="in_review">in_review</option>
          <option value="approved">approved</option>
          <option value="approved_with_note">approved_with_note</option>
          <option value="held">held</option>
          <option value="needs_retry">needs_retry</option>
          <option value="delivered">delivered</option>
          <option value="feedback_requested">feedback_requested</option>
        </select>
        <select
          value={filters.deliveryStatus}
          onChange={(e) => setField('deliveryStatus', e.target.value)}
          className="input-field"
        >
          <option value="">Any delivery status</option>
          <option value="ready_for_review">ready_for_review</option>
          <option value="approved_internal">approved_internal</option>
          <option value="delivered">delivered</option>
          <option value="held">held</option>
          <option value="failed">failed</option>
          <option value="retry_required">retry_required</option>
        </select>
        <select
          value={filters.autoGateStatus}
          onChange={(e) => setField('autoGateStatus', e.target.value)}
          className="input-field"
        >
          <option value="">Any auto-gate status</option>
          <option value="not_evaluated">not_evaluated</option>
          <option value="needs_review">needs_review</option>
          <option value="auto_approved">auto_approved</option>
          <option value="held">held</option>
          <option value="retry_required">retry_required</option>
          <option value="failed">failed</option>
        </select>
        <div className="flex gap-2">
          <select
            value={filters.limit}
            onChange={(e) => setField('limit', Number(e.target.value))}
            className="input-field"
          >
            <option value={25}>25 items</option>
            <option value={50}>50 items</option>
            <option value={100}>100 items</option>
          </select>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="btn-secondary whitespace-nowrap disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminReviewFilters;

export function itemMatchesTab(
  item: { workflow_status?: string; delivery_status?: string },
  tab: ReviewStatusTab
): boolean {
  const workflow = item.workflow_status;
  const delivery = item.delivery_status;

  switch (tab) {
    case 'needs_review':
      return workflow === 'needs_review' || delivery === 'ready_for_review';
    case 'in_review':
      return workflow === 'in_review';
    case 'held_retry':
      return (
        workflow === 'held' ||
        workflow === 'needs_retry' ||
        workflow === 'retry_in_progress' ||
        delivery === 'held' ||
        delivery === 'retry_required' ||
        delivery === 'failed'
      );
    case 'approved_internal':
      return delivery === 'approved_internal';
    case 'delivered':
      return delivery === 'delivered' || workflow === 'delivered';
    case 'feedback':
      return workflow === 'feedback_requested' || workflow === 'feedback_received';
    case 'all':
    default:
      return true;
  }
}
